import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from "@mui/material";
import { SliceTooltipProps } from "@nivo/line";
import {
  IconCaretDown,
  IconCaretUp,
  IconChartLine,
  IconMinus,
  Icon,
} from "@tabler/icons-react";
import { find, groupBy, map, some, sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import Linha from "../../util/charts/linha";
import PercentualText from "../../util/ui/inputs/percentualText";
import Functions from "../../../callable/functions";
import {
  Rentabilidade,
  RentabilidadeBovespa,
} from "../../../models/analises/rentabilidade";
import FelixCard from "../../util/ui/felixCard";
import ChartContainer from "../../util/charts/chartContainer";
import usePromise from "../../util/usePromise";
import { felixTokens, radius } from "../../../theme";

export default function CardRentabilidadeDiaria() {
  const theme = useTheme();
  const obterRentabilidadePromise = useCallback(
    () =>
      new Functions()
        .obterRentabilidade()
        .then((res) => ({
          rentabilidade: map(res.data.carteira, Rentabilidade.fromJson),
          rentabilidadeBovespa: map(
            res.data.ibov,
            RentabilidadeBovespa.fromJson
          ),
        }))
        .then(({ rentabilidade, rentabilidadeBovespa }) => {
          const rentabilidadePorDia = groupBy(rentabilidade, (p) =>
            p.data.format("YYYY-MM-DD")
          );
          const rentabilidadeAoDia = map(rentabilidadePorDia, (v, data) => ({
            data,
            rentabilidade: some(v)
              ? sumBy(v, (r) => (1 + r.variacao) * r.peso) /
                sumBy(v, (r) => r.peso)
              : 1,
            rentabilidadeBovespa: find(
              rentabilidadeBovespa,
              (rb) => rb.data.format("YYYY-MM-DD") === data
            )
              ? 1 +
                find(
                  rentabilidadeBovespa,
                  (rb) => rb.data.format("YYYY-MM-DD") === data
                )!.variacao
              : 0,
          }));
          let rent = [];
          let rentBovespa = [];
          let rentabilidadeAcumulada = 1;
          let rentabilidadeBovespaAcumulada = 1;
          for (let variacaoDia of rentabilidadeAoDia) {
            rentabilidadeAcumulada =
              variacaoDia.rentabilidade * rentabilidadeAcumulada;
            rent.push({
              x: variacaoDia.data,
              y: rentabilidadeAcumulada - 1,
            });
            rentabilidadeBovespaAcumulada =
              variacaoDia.rentabilidadeBovespa * rentabilidadeBovespaAcumulada;
            rentBovespa.push({
              x: variacaoDia.data,
              y: rentabilidadeBovespaAcumulada - 1,
            });
          }

          return [
            // Benchmark em tinta neutra, carteira em accent-ink: ambos legíveis
            // no light e no dark (`primary.light` sumia sobre papel claro).
            {
              id: "IBOV",
              color: alpha(theme.palette.text.primary, 0.35),
              data: rentBovespa,
            },
            {
              id: "Carteira",
              color: theme.palette.accentInk,
              data: rent,
            },
          ];
        }),
    []
  );
  const {
    data = [
      {
        id: "IBOV",
        color: alpha(theme.palette.text.primary, 0.35),
        data: [],
      },
      {
        id: "Carteira",
        color: theme.palette.accentInk,
        data: [],
      },
    ],
    loading,
  } = usePromise(obterRentabilidadePromise);

  const formatTooltip = ({ slice }: SliceTooltipProps) => {
    const variacao = slice.points[0].data.y as number;
    const corVariacao =
      variacao > 0
        ? theme.palette.success.main
        : variacao < 0
        ? theme.palette.error.main
        : theme.palette.text.secondary;
    const IconeVariacao: Icon =
      variacao > 0 ? IconCaretUp : variacao < 0 ? IconCaretDown : IconMinus;
    return (
      // Tooltip de gráfico: surface + hairline vindos do Card do tema.
      <Card
        elevation={0}
        sx={{ boxShadow: felixTokens.shadow.overlay, borderRadius: radius.sm }}
      >
        <Box display="flex" flexDirection="column" padding={1.5} paddingBottom={0}>
          <Typography variant="labelMono" color="text.secondary">
            {moment(slice.points[0].data.x).format("DD [de] MMMM [de] YYYY")}
          </Typography>
        </Box>
        <Box
          key="variacao"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          paddingLeft={1.5}
          paddingRight={1.5}
          paddingBottom={1.5}
          paddingTop={0.5}
        >
          <IconeVariacao size={12} strokeWidth={3} color={corVariacao} />
          <Typography
            variant="dataMono"
            sx={{ fontWeight: 500, color: corVariacao }}
          >
            {<PercentualText value={variacao} decimalScale={2} />}
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <FelixCard>
      <CardHeader
        title="Rentabilidade acumulada"
        avatar={<IconChartLine size={16} />}
      />
      <CardContent>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={data.length === 0}
          isLoading={loading}
        >
          <Linha
            data={data}
            tooltip={formatTooltip}
            yFormat={(value) => `${((value as number) * 100).toFixed(0)}%`}
          />
        </ChartContainer>
      </CardContent>
    </FelixCard>
  );
}
